<template>
  <v-card :class="cropperOpen ? '' : 'hide'" elevation="0">
    <v-overlay
      :model-value="gifProcessing"
      contained
      class="d-flex align-center justify-center"
    >
      <v-progress-circular indeterminate color="primary" />
    </v-overlay>
    <VuePictureCropper
      style="max-height: 600px"
      ref="cropper"
      :src="media.url"
      :aspectRatio="aspectRatio || 16 / 9"
      :autoCropArea="1"
      :zoomOnWheel="false"
      :background="noBackground"
      :modal="false"
    />
    <v-card-actions class="d-flex justify-center">
      <v-btn icon @click="confirmCrop" :loading="gifProcessing">
        <v-icon>mdi-check</v-icon>
      </v-btn>
      <v-btn icon @click="rotateRight">
        <v-icon>mdi-rotate-right</v-icon>
      </v-btn>
      <v-btn icon @click="rotateLeft">
        <v-icon>mdi-rotate-left</v-icon>
      </v-btn>
      <v-btn icon @click="zoomIn">
        <v-icon>mdi-magnify-plus-outline</v-icon>
      </v-btn>
      <v-btn icon @click="zoomOut">
        <v-icon>mdi-magnify-minus-outline</v-icon>
      </v-btn>

      <v-tooltip location="bottom">
        <template #activator="{ props }">
          <v-btn icon v-bind="props" @click="noBackground = !noBackground">
            <v-icon v-if="noBackground">mdi-invert-colors-off</v-icon>
            <v-icon v-else>mdi-invert-colors</v-icon>
          </v-btn>
        </template>
        {{
          noBackground
            ? $t('gallery.showBackgroundColor')
            : $t('gallery.hideBackgroundColor')
        }}
      </v-tooltip>

      <v-btn
        v-if="!noBackground"
        :style="{ backgroundColor: openColorPicker ? '#e0e0e0' : null }"
        icon
        @click="openColorPicker = !openColorPicker"
      >
        <v-icon>mdi-format-color-fill</v-icon>
      </v-btn>

      <v-btn v-if="cropped" icon @click="cropperOpen = false">
        <v-icon>mdi-undo</v-icon>
      </v-btn>
      <v-color-picker
        v-if="openColorPicker && !noBackground"
        v-model="color"
        dot-size="25"
        swatches-max-height="200"
        style="
          position: fixed;
          top: 0;
          left: 50%;
          transform: translateX(-50%);
          z-index: 9999;
        "
      />

      <v-btn icon @click="cropperOpen = false">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script setup>
import { ref, watch, onMounted, onBeforeUnmount, computed } from 'vue'
import VuePictureCropper from 'vue-cropperjs'
import 'cropperjs/dist/cropper.css'
import { cropAnimatedGif, isAnimatedGif } from '@/helpers/gifCropper'

const props = defineProps({
  cropped: Boolean,
  media: { type: Object, required: true },
  aspectRatio: Number,
  modelValue: Boolean,
})

const emit = defineEmits([
  'update:modelValue',
  'crop-confirmed',
  'cropper-mounted',
])

const cropper = ref(null)
const color = ref('#000000')
const openColorPicker = ref(false)
const noBackground = ref(true)
const isGif = ref(false)
const gifProcessing = ref(false)

const cropperOpen = computed({
  get: () => props.modelValue,
  set: val => emit('update:modelValue', val),
})

// Detect animated GIF on mount / when media changes
watch(
  () => props.media?.url,
  async url => {
    if (url && props.media?.mime?.includes('gif')) {
      isGif.value = await isAnimatedGif(url)
    } else {
      isGif.value = false
    }
  },
  { immediate: true }
)

function zoomIn() {
  cropper.value?.relativeZoom(0.1)
}
function zoomOut() {
  cropper.value?.relativeZoom(-0.1)
}
function rotateLeft() {
  cropper.value?.rotate(-90)
}
function rotateRight() {
  cropper.value?.rotate(90)
}

async function confirmCrop() {
  const cropperInstance = cropper.value
  if (!cropperInstance) return

  // If this is an animated GIF, use the special GIF cropper
  if (isGif.value) {
    return confirmGifCrop(cropperInstance)
  }

  const cropperData = {
    width: 1200,
    height: 850,
    fillColor: noBackground.value ? undefined : color.value,
  }
  const canvas = await cropperInstance.getCroppedCanvas(cropperData)
  const dataUrl = canvas.toDataURL('image/jpeg')
  const croppedBlob = await new Promise(resolve => {
    canvas.toBlob(blob => {
      resolve(blob)
    }, 'image/jpeg')
  })
  const blobSizeInBytes = croppedBlob.size
  const blobSizeInMB = blobSizeInBytes / (1024 * 1024)
  console.log(blobSizeInMB)

  emit('crop-confirmed', {
    file: croppedBlob,
    url: dataUrl,
    name: props.media.name,
    mime: 'image/jpeg',
    fillColor: noBackground.value ? false : color.value,
  })

  cropperOpen.value = false
}

async function confirmGifCrop(cropperInstance) {
  try {
    gifProcessing.value = true

    // Get crop box data relative to the *natural* image
    const cropBoxData = cropperInstance.getData(true) // rounded, in natural pixels

    const cropRegion = {
      x: cropBoxData.x,
      y: cropBoxData.y,
      width: cropBoxData.width,
      height: cropBoxData.height,
    }

    const { blob, dataUrl } = await cropAnimatedGif(
      props.media.url,
      cropRegion,
      {
        outputWidth: Math.min(cropRegion.width, 1200),
        outputHeight: Math.min(
          cropRegion.height,
          Math.round((850 / 1200) * Math.min(cropRegion.width, 1200))
        ),
      }
    )

    const blobSizeInMB = blob.size / (1024 * 1024)
    console.log('GIF size:', blobSizeInMB, 'MB')

    emit('crop-confirmed', {
      file: blob,
      url: dataUrl,
      name: props.media.name,
      mime: 'image/gif',
      fillColor: false,
    })

    cropperOpen.value = false
  } catch (err) {
    console.error('GIF crop failed:', err)
  } finally {
    gifProcessing.value = false
  }
}

function onEnter(e) {
  if (e.key === 'Enter') confirmCrop()
}

onMounted(() => {
  if (props.media.fillColor) {
    color.value = props.media.fillColor
    noBackground.value = false
  }
  emit('cropper-mounted', cropper.value)
  window.addEventListener('keyup', onEnter)
})

onBeforeUnmount(() => {
  window.removeEventListener('keyup', onEnter)
})
</script>

<style lang="scss">
.cropper-controls {
  border: 3px solid transparent;
  background:
    linear-gradient(#ffffff 0 0) padding-box,
    var(--linearHotPinkDodgerBlue) border-box;
  border-radius: 13px;
  overflow: hidden;
}
.cropper {
  .hide {
    display: none !important;
  }
}
</style>
